import { useState, useEffect, useMemo } from "react";
import { useSWRConfig } from "swr";
import { fetchOurApi } from "../../../../../../helpers/utils/apiCalls";
import { getAccountId } from "../../../../../../helpers/accountHelpers";
import { useCachedFirebaseCrud } from "../../../../../../helpers/firebase";

const buildPrimaryVendorPayload = (vendor) => ({
  id: vendor.relationshipId,
  vendorId: vendor.id,
  name: vendor.name,
  vendorData: {
    naicsCode: vendor.naicsCode,
    naicsTitle: vendor.naicsTitle,
    tonsCo2ePerDollar: vendor.tonsCo2ePerDollar,
    isConfirmed: vendor.isConfirmed,
    confidenceScore: vendor.confidenceScore
  }
});

const buildSecondaryVendorPayload = (vendor) => ({
  vendorId: vendor.id,
  relationshipId: vendor.relationshipId,
  name: vendor.name
});

const buildDismissPairPayload = (companyId, pair) => ({
  companyId,
  relationshipAId: pair.a.relationshipId,
  relationshipBId: pair.b.relationshipId,
  vendorAId: pair.a.vendorId,
  vendorBId: pair.b.vendorId
});

const callApi = async (path, data) => {
  const response = await fetchOurApi({ path, method: "POST", data });

  if (!response) {
    throw new Error(`No response from ${path}`);
  }
  if (response.error) {
    const message =
      typeof response.error === "string"
        ? response.error
        : response.message || `API error from ${path}`;
    throw new Error(message);
  }
  return response;
};

const dismissPair = (companyId, pair) =>
  callApi(
    "/vendors/dismiss-buyer-vendor-pair",
    buildDismissPairPayload(companyId, pair)
  );

const vendorToPairMember = (vendor) => ({
  vendorId: vendor.id,
  relationshipId: vendor.relationshipId
});

const SECTION_LABELS = ["duplicate vendors", "industry classifications", "empty vendors"];

const buildPartialFailureMessage = (results) => {
  const failedLabels = results
    .map((r, i) => (r.status === "rejected" ? SECTION_LABELS[i] : null))
    .filter(Boolean);
  return failedLabels.length > 0
    ? `Could not load: ${failedLabels.join(", ")}`
    : null;
};

export const useCleanupCandidates = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [clusters, setClusters] = useState([]);
  const [lowConfidenceNaics, setLowConfidenceNaics] = useState([]);
  const [emptyVendors, setEmptyVendors] = useState([]);
  const [zeroTxnPairs, setZeroTxnPairs] = useState([]);
  const [error, setError] = useState(null);

  const { updateCollectionDoc } = useCachedFirebaseCrud();
  const { mutate } = useSWRConfig();

  const invalidateVendorCaches = () => {
    mutate("vendor-relationships");
    mutate("transactions");
  };

  useEffect(() => {
    let isMounted = true;

    const loadCandidates = async () => {
      const companyId = getAccountId();

      const results = await Promise.allSettled([
        callApi("/vendors/detect-buyer-duplicates", { companyId }),
        callApi("/vendors/detect-low-confidence-naics", { companyId }),
        callApi("/vendors/detect-empty-vendors", { companyId })
      ]);

      if (!isMounted) {
        return;
      }

      const [duplicatesResult, naicsResult, emptyResult] = results;

      if (duplicatesResult.status === "fulfilled") {
        const { clusters: nextClusters = [], zeroTxnPairs: nextZeroTxnPairs = [] } =
          duplicatesResult.value || {};
        setClusters(nextClusters.map((c) => ({ ...c, status: "pending" })));
        setZeroTxnPairs(
          nextZeroTxnPairs.map((p) => ({ ...p, status: "pending" }))
        );
      }
      if (naicsResult.status === "fulfilled") {
        const next = naicsResult.value?.lowConfidenceNaics || [];
        setLowConfidenceNaics(
          next
            .filter((v) => (v.transactionCount || 0) > 0)
            .map((v) => ({ ...v, status: "pending" }))
        );
      }
      if (emptyResult.status === "fulfilled") {
        const next = emptyResult.value?.emptyVendors || [];
        setEmptyVendors(next.map((v) => ({ ...v, status: "pending" })));
      }

      const allFailed = results.every(({ status }) => status === "rejected");
      if (allFailed) {
        setError("Failed to load cleanup candidates");
      } else {
        const partialMessage = buildPartialFailureMessage(results);
        if (partialMessage) {
          setError(partialMessage);
        }
      }

      setIsLoading(false);
    };

    loadCandidates();

    return () => {
      isMounted = false;
    };
  }, []);

  const mergeCluster = async ({
    clusterId,
    primaryId,
    keptIds,
    rejectedPairs = [],
    naicsOverride
  }) => {
    const companyId = getAccountId();
    const cluster = clusters.find((c) => c.clusterId === clusterId);

    if (!cluster) {
      setError("Cluster not found — refresh and try again");
      return;
    }

    const primaryVendorData = cluster.vendors.find((v) => v.id === primaryId);
    const secondaryClusterVendors = cluster.vendors.filter(
      (v) => v.id !== primaryId && keptIds.includes(v.id)
    );

    try {
      await callApi("/transactions/merge-vendor-transactions", {
        companyId,
        primaryVendor: buildPrimaryVendorPayload(primaryVendorData),
        secondaryVendors: secondaryClusterVendors.map(
          buildSecondaryVendorPayload
        ),
        ...(naicsOverride ? { naicsOverride } : {})
      });

      const dismissResults = await Promise.allSettled(
        rejectedPairs.map((pair) => dismissPair(companyId, pair))
      );

      setClusters((current) =>
        current.map((c) =>
          c.clusterId === clusterId ? { ...c, status: "merged" } : c
        )
      );
      invalidateVendorCaches();

      const dismissFailures = dismissResults.filter(
        ({ status }) => status === "rejected"
      );
      if (dismissFailures.length > 0) {
        setError(
          `Merge succeeded but ${dismissFailures.length} pair dismissal(s) failed — refresh to retry`
        );
      }
    } catch (err) {
      setError(err.message || "Failed to merge cluster");
    }
  };

  const dismissCluster = async (clusterId, { rejectedPairs = [] } = {}) => {
    const companyId = getAccountId();
    const cluster = clusters.find((c) => c.clusterId === clusterId);

    if (!cluster) {
      setError("Cluster not found — refresh and try again");
      return;
    }

    const { vendors } = cluster;
    const allPairs = vendors.flatMap((vendorA, i) =>
      vendors.slice(i + 1).map((vendorB) => ({
        a: vendorToPairMember(vendorA),
        b: vendorToPairMember(vendorB)
      }))
    );

    const dismissResults = await Promise.allSettled(
      [...allPairs, ...rejectedPairs].map((pair) =>
        dismissPair(companyId, pair)
      )
    );

    setClusters((current) =>
      current.map((c) =>
        c.clusterId === clusterId ? { ...c, status: "dismissed" } : c
      )
    );

    const dismissFailures = dismissResults.filter(
      ({ status }) => status === "rejected"
    );
    if (dismissFailures.length > 0) {
      setError(
        `Dismissed cluster but ${dismissFailures.length} pair(s) failed to persist — refresh to retry`
      );
    }
  };

  const clearError = () => setError(null);

  const updateNaics = async ({
    relationshipId,
    vendorId,
    vendorName,
    emissionCategory,
    scopeThreeCategory,
    naicsCode,
    naicsTitle,
    tonsCo2ePerDollar
  }) => {
    const companyId = getAccountId();

    try {
      await updateCollectionDoc("vendor-relationships", relationshipId, {
        "vendorData.naicsCode": naicsCode,
        "vendorData.naicsTitle": naicsTitle,
        "vendorData.tonsCo2ePerDollar": tonsCo2ePerDollar,
        "vendorData.confidenceScore": 1,
        "vendorData.isConfirmed": true
      });

      await callApi("/transactions/update-vendor-transactions", {
        companyId,
        id: relationshipId,
        vendorId,
        vendorName,
        emissionCategory: emissionCategory || "spend-based",
        scopeThreeCategory,
        naicsCode,
        naicsTitle,
        tonsCo2ePerDollar
      });

      setLowConfidenceNaics((current) =>
        current.map((v) =>
          v.relationshipId === relationshipId
            ? {
                ...v,
                status: "updated",
                naicsCode,
                naicsTitle,
                tonsCo2ePerDollar,
                confidenceScore: 1,
                isConfirmed: true
              }
            : v
        )
      );
      invalidateVendorCaches();
    } catch (err) {
      setError(err.message || "Failed to update industry");
    }
  };

  const dismissNaics = async (relationshipId) => {
    try {
      await updateCollectionDoc("vendor-relationships", relationshipId, {
        "vendorData.isConfirmed": true
      });

      setLowConfidenceNaics((current) =>
        current.map((v) =>
          v.relationshipId === relationshipId
            ? { ...v, status: "dismissed", isConfirmed: true }
            : v
        )
      );
    } catch (err) {
      setError(err.message || "Failed to dismiss industry review");
    }
  };

  const archiveEmptyVendor = async (relationshipId) => {
    try {
      await updateCollectionDoc("vendor-relationships", relationshipId, {
        archived: true
      });

      setEmptyVendors((current) =>
        current.map((v) =>
          v.relationshipId === relationshipId
            ? { ...v, status: "archived" }
            : v
        )
      );
    } catch (err) {
      setError(err.message || "Failed to archive vendor");
    }
  };

  const archiveZeroTxnPairs = async (pairIds) => {
    if (!pairIds?.length) {
      return;
    }
    try {
      await callApi("/vendors/archive-zero-txn-pairs", {
        companyId: getAccountId(),
        pairIds
      });

      setZeroTxnPairs((current) =>
        current.map((p) =>
          pairIds.includes(p.pairId) ? { ...p, status: "archived" } : p
        )
      );
      invalidateVendorCaches();
    } catch (err) {
      setError(err.message || "Failed to archive empty-duplicate vendors");
    }
  };

  const counts = useMemo(
    () => ({
      duplicateClusterCount: clusters.filter(
        ({ status }) => status === "pending"
      ).length,
      lowConfidenceNaicsCount: lowConfidenceNaics.filter(
        ({ status }) => status === "pending"
      ).length,
      emptyVendorCount: emptyVendors.filter(
        ({ status }) => status === "pending"
      ).length,
      zeroTxnPairCount: zeroTxnPairs.filter(
        ({ status }) => status === "pending"
      ).length,
      totalVendorsFlagged:
        clusters
          .filter(({ status }) => status === "pending")
          .reduce((sum, { vendors }) => sum + vendors.length, 0) +
        lowConfidenceNaics.filter(({ status }) => status === "pending").length +
        emptyVendors.filter(({ status }) => status === "pending").length
    }),
    [clusters, lowConfidenceNaics, emptyVendors, zeroTxnPairs]
  );

  return {
    isLoading,
    error,
    clearError,
    data: {
      duplicateClusters: clusters,
      lowConfidenceNaics,
      emptyVendors,
      zeroTxnPairs
    },
    counts,
    mergeCluster,
    dismissCluster,
    updateNaics,
    dismissNaics,
    archiveEmptyVendor,
    archiveZeroTxnPairs
  };
};
